import { put, select, takeLeading } from 'redux-saga/effects';
import {
	ADD_TRANSLATION_LANGUAGE,
	AddTranslationLanguageAction,
	CHANGE_BUTTONS_TRANSLATE_INFO,
	CHANGE_MULTIPLE_OPINION_SECTION_TRANSLATE_INFO,
	CHANGE_MULTIPLE_OPINION_STROKE_TRANSLATE_INFO,
	CHANGE_OPTION_TRANSLATE_INFO,
	CHANGE_QUESTION_SETTING_TRANSLATE_INFO,
	CHANGE_QUESTION_TRANSLATE_INFO,
	CHANGE_SECTION_TRANSLATE_INFO,
	ChangeButtonsTranslateInfo,
	ChangeMultipleOpinionSectionTranslateInfo,
	ChangeMultipleOpinionStrokeTranslateInfo,
	ChangeOptionTranslateInfo,
	ChangeQuestionSettingTranslateInfo,
	ChangeQuestionTranslateInfo,
	ChangeSectionTranslateInfo,
	DELETE_TRANSLATION_LANGUAGE,
	DeleteTranslationLanguageAction,
	GET_ALL_TRANSLATED_LANGUAGES,
	GET_DEFAULT_BUTTONS_DATA,
	GET_GOOGLE_LANGUAGES_LIST,
	GET_TRANSLATED_BUTTONS_DATA,
	GET_TRANSLATED_DATA,
	GetAllTranslatedLanguagesAction,
	GetDefaultButtonsDataAction,
	GetGoogleLanguagesListAction,
	GetTranslatedButtonsDataAction,
	GetTranslatedDataAction,
	POST_TRANSLATE_BUTTONS_DATA,
	PostTranslateButtonsData,
	UPDATE_LANGUGAE_STATUS,
	UpdateLangugaeStatus,
	UPLOAD_MULTI_LINGUAL_CSV,
	UploadMultiLingualCsv,
} from './types';
import {
	addTranslationLanguageAction,
	changeMultipleOpinionSectionTranslateInfo,
	changeMultipleOpinionStrokeTranslateInfo,
	changeOptionTranslateInfo,
	changeQuestionSettingTranslateInfo,
	changeQuestionTranslateInfo,
	deleteTranslationLanguageAction,
	getAllTranslatedLanguagesAction,
	getDefaultButtonsDataAction,
	getGoogleLanguagesListAction,
	getTranslatedButtonsDataAction,
	getTranslatedDataAction,
	postTranslateButtonsData,
	saveTranslationLanguageAction,
	setAllTranslatedLanguagesAction,
	setDefaultButtonsDataAction,
	setGoogleLanguagesListAction,
	setTranslatedButtonDataAction,
	setTranslatedButtonsInfo,
	setTranslatedDataAction,
	setTranslatedQuestionInfo,
	updateLangugaeStatus,
	uploadMultiLingualCsv,
} from './actions';
import { putEffect } from 'shared/utils/putEffect';
import { fulfill, pend, reject } from 'store/promise/actions';
import { getApiError } from 'shared/utils/getApiError';
import { getApiEndPointUrl } from 'shared/utils/getApiEndpointUrl';
import axios from '@utils/axios';
import {
	addTranslationLanguageEndpoint,
	defaultButtonsDataEndpoint,
	deleteTranslationLanguageEndpoint,
	editTranslatedButtonsDataEndpoint,
	goggleLanguagesListEndpoint,
	updateLanguageStatusEndpoint,
	updateMultipleOpinionSectionTextEndpoint,
	updateTranslatedOptionHeadersEndpoint,
	updateTranslatedQuestionHeadersEndpoint,
	updateTranslatedSectionButtonSettingEndpoint,
	updateTranslatedSectionHeadersEndpoint,
	updateTranslatedSectionSettingEndpoint,
	uploadMultiLingualCsvEndpoint,
} from './const';
import { notification } from 'antd';
import { assign } from 'shared/utils/assign';
import { set } from 'shades';

function* getAllTranslatedLanguagesListSaga(action: GetAllTranslatedLanguagesAction) {
	const { surveyId } = action.payload;
	try {
		yield putEffect(pend(getAllTranslatedLanguagesAction));
		const { data } = yield axios.get(getApiEndPointUrl('questionnaires', surveyId, 'languages'));
		yield putEffect(setAllTranslatedLanguagesAction(data));
		yield putEffect(fulfill(getAllTranslatedLanguagesAction));
	} catch (e: any) {
		yield putEffect(reject(getAllTranslatedLanguagesAction, getApiError(e)));
	}
}

function* getGoogleLanguagesListSaga(action: GetGoogleLanguagesListAction) {
	try {
		yield putEffect(pend(getGoogleLanguagesListAction));
		const { data } = yield axios.get(getApiEndPointUrl(goggleLanguagesListEndpoint));
		yield putEffect(setGoogleLanguagesListAction(data));
		yield putEffect(fulfill(getGoogleLanguagesListAction));
	} catch (e: any) {
		yield putEffect(reject(getGoogleLanguagesListAction, getApiError(e)));
	}
}

function* addTranslationLanguageSaga(action: AddTranslationLanguageAction) {
	const { languages, surveyId, auto_translate } = action.payload;
	const langCodes = languages.map((item) => item.code);
	try {
		yield putEffect(pend(addTranslationLanguageAction));
		yield axios.post(getApiEndPointUrl(addTranslationLanguageEndpoint), {
			surveyId,
			languages: langCodes,
			auto_translate,
		});
		if (!auto_translate) {
			yield putEffect(postTranslateButtonsData(surveyId));
		}
		yield putEffect(saveTranslationLanguageAction(languages));

		notification.success({
			message: 'Selected languages have been successfully added.',
			closeIcon: false,
			duration: 3,
			placement: 'bottomRight',
		});
		yield putEffect(fulfill(addTranslationLanguageAction));
	} catch (e: any) {
		yield putEffect(reject(addTranslationLanguageAction, getApiError(e)));
	}
}

function* deleteTranslationLanguageSaga(action: DeleteTranslationLanguageAction) {
	const { surveyId, langCode } = action.payload;
	try {
		yield axios.delete(getApiEndPointUrl(deleteTranslationLanguageEndpoint, surveyId, langCode));
		notification.success({
			message: 'Survey language deleted successfully.',
			closeIcon: false,
			duration: 3,
			placement: 'bottomRight',
		});
	} catch (e: any) {
		yield putEffect(reject(deleteTranslationLanguageAction, getApiError(e)));
	}
}

function* getTranslatedDataSaga(action: GetTranslatedDataAction) {
	const { surveyId, langCode } = action.payload;
	try {
		yield putEffect(pend(getTranslatedDataAction));
		const { data } = yield axios.get(getApiEndPointUrl('questionnaires', surveyId, 'translated', langCode));
		yield putEffect(setTranslatedDataAction(data));
		yield putEffect(fulfill(getTranslatedDataAction));
	} catch (e: any) {
		yield putEffect(reject(getTranslatedDataAction, getApiError(e)));
	}
}

function* changeQuestionTranslateInfoSaga(action: ChangeQuestionTranslateInfo) {
	const { questionId, content } = action.payload;
	const { currentEditingLanguageQues } = yield select((s) => s.multiLingual);
	const question = currentEditingLanguageQues[questionId];
	if (!!question) {
		try {
			yield putEffect(setTranslatedQuestionInfo(questionId, assign(content, question)));

			yield putEffect(pend(changeQuestionTranslateInfo));
			yield axios.patch(getApiEndPointUrl(updateTranslatedQuestionHeadersEndpoint, questionId), action.payload);

			yield putEffect(fulfill(changeQuestionTranslateInfo));
		} catch (e: any) {
			yield putEffect(reject(changeQuestionTranslateInfo, getApiError(e)));
		}
	} else {
		let question: any;
		for (const id in currentEditingLanguageQues) {
			const section = currentEditingLanguageQues[id];
			if (section?.metaData && section?.questionnaireId.length > 0) {
				if (section?.questions?.[questionId]) {
					question = section?.questions?.[questionId];
					break;
				}
			}
		}
		try {
			currentEditingLanguageQues[question?.questionnaireSectionId].questions[question.id] = assign(content, question);
			yield axios.patch(getApiEndPointUrl(updateTranslatedQuestionHeadersEndpoint, questionId), action.payload);
			yield putEffect(
				setTranslatedQuestionInfo(
					question?.questionnaireSectionId,
					currentEditingLanguageQues[question?.questionnaireSectionId]
				)
			);
		} catch (e: any) {
			yield putEffect(reject(changeQuestionTranslateInfo, getApiError(e)));
		}
	}
}

function* changeOptionTranslateInfoSaga(action: ChangeOptionTranslateInfo) {
	const { questionId, optionId, languageCode, value } = action.payload;
	const { currentEditingLanguageQues: questions } = yield select((s) => s.multiLingual);
	const question = questions[questionId];
	if (!!question) {
		try {
			yield axios.patch(getApiEndPointUrl(updateTranslatedOptionHeadersEndpoint, optionId), {
				questionId,
				languageCode,
				value,
			});
			yield putEffect(setTranslatedQuestionInfo(questionId, set('options', optionId, 'value')(value)(question)));
		} catch (e: any) {
			yield putEffect(reject(changeOptionTranslateInfo, getApiError(e)));
		}
	} else {
		let question: any;
		for (const id in questions) {
			const section = questions[id];
			if (section?.metaData && section?.questionnaireId.length > 0) {
				if (section?.questions?.[questionId]) {
					question = section?.questions?.[questionId];
					break;
				}
			}
		}
		try {
			questions[question?.questionnaireSectionId].questions[question.id] = set('options', optionId, 'value')(value)(
				question
			);
			yield axios.patch(getApiEndPointUrl(updateTranslatedOptionHeadersEndpoint, optionId), {
				questionId,
				languageCode,
				value,
			});
			yield putEffect(
				setTranslatedQuestionInfo(question?.questionnaireSectionId, questions[question?.questionnaireSectionId])
			);
		} catch (e: any) {
			yield putEffect(reject(changeOptionTranslateInfo, getApiError(e)));
		}
	}
}

function* changeSectionTranslateInfoSaga(action: ChangeSectionTranslateInfo) {
	try {
		const { id } = action.payload;
		yield axios.patch(getApiEndPointUrl(updateTranslatedSectionHeadersEndpoint, id), action.payload);
	} catch (e: any) {
		yield putEffect(reject(changeQuestionTranslateInfo, getApiError(e)));
	}
}

function* changeQuestionSettingTranslateInfoWatcherSaga(action: ChangeQuestionSettingTranslateInfo) {
	const { questionId, settings, isSection, ...restPayload } = action.payload;
	const { currentEditingLanguageQues: questions } = yield select((s) => s.multiLingual);
	const payloadWithoutIsSection = {
		...(isSection ? { sectionId: questionId } : { questionId }),
		...restPayload,
		settings,
	};
	if (!!questions[questionId]) {
		try {
			let question = questions[questionId];
			question = {
				...question,
				settings: {
					...question.settings,
					...settings,
				},
			};
			if (!!isSection) {
				yield axios.patch(getApiEndPointUrl(updateTranslatedSectionButtonSettingEndpoint), payloadWithoutIsSection);
			} else {
				yield axios.patch(getApiEndPointUrl(updateTranslatedSectionSettingEndpoint), payloadWithoutIsSection);
			}
			yield putEffect(setTranslatedQuestionInfo(questionId, assign({ settings }, question)));
		} catch (e: any) {
			yield putEffect(reject(changeQuestionSettingTranslateInfo, getApiError(e)));
		}
	} else {
		let question: any;
		for (const id in questions) {
			const section = questions[id];
			if (section?.metaData && section?.questionnaireId.length > 0) {
				if (section?.questions?.[questionId]) {
					question = section?.questions?.[questionId];
					break;
				}
			}
		}
		question = {
			...question,
			settings: {
				...question.settings,
				...settings,
			},
		};
		try {
			yield axios.patch(getApiEndPointUrl(updateTranslatedSectionSettingEndpoint), action.payload);
			questions[question?.questionnaireSectionId].questions[question.id] = assign({ settings }, question);
			yield putEffect(
				setTranslatedQuestionInfo(question?.questionnaireSectionId, questions[question?.questionnaireSectionId])
			);
		} catch (e: any) {
			yield putEffect(reject(changeQuestionSettingTranslateInfo, getApiError(e)));
		}
	}
}

function* changeMultipleOpinionSectionTranslateInfoSaga(action: ChangeMultipleOpinionSectionTranslateInfo) {
	const { sectionId, ...rest } = action.payload;
	const { questionId, content } = action.payload;
	const { currentEditingLanguageQues: questions } = yield select((s) => s.multiLingual);
	const question = questions[questionId];
	if (!!question) {
		try {
			yield axios.put(getApiEndPointUrl(updateMultipleOpinionSectionTextEndpoint, sectionId), rest);
			yield putEffect(
				setTranslatedQuestionInfo(questionId, set('sections', sectionId, 'text')(content?.text)(question))
			);
		} catch (e: any) {
			yield putEffect(reject(changeMultipleOpinionSectionTranslateInfo, getApiError(e)));
		}
	} else {
		let question: any;
		for (const id in questions) {
			const section = questions[id];
			if (section?.metaData && section?.questionnaireId.length > 0) {
				if (section?.questions?.[questionId]) {
					question = section?.questions?.[questionId];
					break;
				}
			}
		}
		try {
			yield axios.put(getApiEndPointUrl(updateMultipleOpinionSectionTextEndpoint, sectionId), rest);
			questions[question?.questionnaireSectionId].questions[question.id] = set(
				'sections',
				sectionId,
				'text'
			)(content?.text)(question);
			yield putEffect(
				setTranslatedQuestionInfo(question?.questionnaireSectionId, questions[question?.questionnaireSectionId])
			);
		} catch (e: any) {
			yield putEffect(reject(changeMultipleOpinionSectionTranslateInfo, getApiError(e)));
		}
	}
}

function* changeMultipleOpinionStrokeTranslateInfoSaga(action: ChangeMultipleOpinionStrokeTranslateInfo) {
	const { sectionId, questionId, text, strokeId, languageCode, content } = action.payload;
	const { currentEditingLanguageQues: questions } = yield select((s) => s.multiLingual);
	const question = questions[questionId];

	const requiredPayload = {
		questionId,
		languageCode,
		content: {
			strokes: Object.values(content).map(({ strokeId, ...rest }: any) => ({
				id: strokeId,
				...rest,
			})),
		},
	};
	if (!!question) {
		try {
			yield axios.put(getApiEndPointUrl(updateMultipleOpinionSectionTextEndpoint, sectionId), requiredPayload);
			yield putEffect(
				setTranslatedQuestionInfo(questionId, set('sections', sectionId, 'strokes', strokeId, 'text')(text)(question))
			);
		} catch (e: any) {
			yield putEffect(reject(changeMultipleOpinionStrokeTranslateInfo, getApiError(e)));
		}
	} else {
		let question: any;
		for (const id in questions) {
			const section = questions[id];
			if (section?.metaData && section?.questionnaireId.length > 0) {
				if (section?.questions?.[questionId]) {
					question = section?.questions?.[questionId];
					break;
				}
			}
		}
		try {
			yield axios.put(getApiEndPointUrl(updateMultipleOpinionSectionTextEndpoint, sectionId), requiredPayload);
			questions[question?.questionnaireSectionId].questions[question.id] = set(
				'sections',
				sectionId,
				'strokes',
				strokeId,
				'text'
			)(text)(question);
			yield putEffect(
				setTranslatedQuestionInfo(questionId, set('sections', sectionId, 'strokes', strokeId, 'text')(text)(question))
			);
		} catch (e: any) {
			yield putEffect(reject(changeMultipleOpinionStrokeTranslateInfo, getApiError(e)));
		}
	}
}

function* uploadMultiLingualCsvSaga(action: UploadMultiLingualCsv) {
	const { file, languageCode, questionnaireId } = action.payload;
	const formData = new FormData();
	formData.append('file', file);
	formData.append('languageCode', languageCode);
	formData.append('questionnaireId', questionnaireId);
	try {
		yield putEffect(pend(uploadMultiLingualCsv));
		const { data } = yield axios.post(getApiEndPointUrl(uploadMultiLingualCsvEndpoint), formData);
		yield putEffect(setTranslatedDataAction(data?.questionnaire));
		yield putEffect(fulfill(uploadMultiLingualCsv));
	} catch (e: any) {
		yield putEffect(reject(uploadMultiLingualCsv, getApiError(e)));
	}
}

function* getDefaultButtonsListSaga(action: GetDefaultButtonsDataAction) {
	try {
		yield putEffect(pend(getDefaultButtonsDataAction));
		const { data } = yield axios.get(getApiEndPointUrl(defaultButtonsDataEndpoint));
		yield putEffect(setDefaultButtonsDataAction(data));
		yield putEffect(fulfill(getDefaultButtonsDataAction));
	} catch (e: any) {
		yield putEffect(reject(getDefaultButtonsDataAction, getApiError(e)));
	}
}

function* getTranslatedButtonsDataSaga(action: GetTranslatedButtonsDataAction) {
	const { surveyId, langCode } = action.payload;
	try {
		yield putEffect(pend(getTranslatedButtonsDataAction));
		const { data } = yield axios.get(getApiEndPointUrl('survey-ui-texts', 'by-survey', surveyId, langCode));
		yield putEffect(setTranslatedButtonDataAction(data));
		yield putEffect(fulfill(getTranslatedButtonsDataAction));
	} catch (e: any) {
		yield putEffect(reject(getTranslatedButtonsDataAction, getApiError(e)));
	}
}

function* addButtonsTranslationLanguageSaga(action: PostTranslateButtonsData) {
	const { surveyId } = action.payload;
	try {
		yield putEffect(pend(postTranslateButtonsData));
		yield axios.post(getApiEndPointUrl('survey-ui-texts', 'addTextByLanguage', surveyId));
		yield putEffect(fulfill(postTranslateButtonsData));
	} catch (e: any) {
		yield putEffect(reject(postTranslateButtonsData, getApiError(e)));
	}
}

function* changeButtonsTranslateInfoSaga(action: ChangeButtonsTranslateInfo) {
	const { surveyId, textId, ...rest } = action.payload;
	try {
		yield axios.put(getApiEndPointUrl(editTranslatedButtonsDataEndpoint, surveyId), rest);
		yield putEffect(setTranslatedButtonsInfo(textId, rest.translated_text));
	} catch (e: any) {
		yield putEffect(reject(changeMultipleOpinionSectionTranslateInfo, getApiError(e)));
	}
}

function* updateLangugaeStatusSaga(action: UpdateLangugaeStatus) {
	const { id, langugaeCode, status } = action.payload;
	try {
		yield putEffect(pend(updateLangugaeStatus));
		yield axios.patch(getApiEndPointUrl(updateLanguageStatusEndpoint, id, langugaeCode), { [status]: true });

		yield putEffect(fulfill(updateLangugaeStatus));
	} catch (e: any) {
		yield putEffect(reject(updateLangugaeStatus, getApiError(e)));
	}
}

function* getAllTranslatedLanguagesListWatcher() {
	yield takeLeading(GET_ALL_TRANSLATED_LANGUAGES, getAllTranslatedLanguagesListSaga);
}

function* getGoogleLanguagesListWatcher() {
	yield takeLeading(GET_GOOGLE_LANGUAGES_LIST, getGoogleLanguagesListSaga);
}

function* addTranslationLanguageWatcher() {
	yield takeLeading(ADD_TRANSLATION_LANGUAGE, addTranslationLanguageSaga);
}

function* deleteTranslationLanguageWatcher() {
	yield takeLeading(DELETE_TRANSLATION_LANGUAGE, deleteTranslationLanguageSaga);
}

function* getTranslatedDataWatcher() {
	yield takeLeading(GET_TRANSLATED_DATA, getTranslatedDataSaga);
}

function* changeQuestionTranslateInfoWatcher() {
	yield takeLeading(CHANGE_QUESTION_TRANSLATE_INFO, changeQuestionTranslateInfoSaga);
}

function* changeOptionTranslateInfoWatcher() {
	yield takeLeading(CHANGE_OPTION_TRANSLATE_INFO, changeOptionTranslateInfoSaga);
}

function* changeSectionTranslateInfoWatcher() {
	yield takeLeading(CHANGE_SECTION_TRANSLATE_INFO, changeSectionTranslateInfoSaga);
}

function* changeQuestionSettingTranslateInfoWatcher() {
	yield takeLeading(CHANGE_QUESTION_SETTING_TRANSLATE_INFO, changeQuestionSettingTranslateInfoWatcherSaga);
}

function* changeMultipleOpinionSectionTranslateInfoWatcher() {
	yield takeLeading(CHANGE_MULTIPLE_OPINION_SECTION_TRANSLATE_INFO, changeMultipleOpinionSectionTranslateInfoSaga);
}

function* changeMultipleOpinionStrokeTranslateInfoWatcher() {
	yield takeLeading(CHANGE_MULTIPLE_OPINION_STROKE_TRANSLATE_INFO, changeMultipleOpinionStrokeTranslateInfoSaga);
}

function* uploadMultiLingualCsvWatcher() {
	yield takeLeading(UPLOAD_MULTI_LINGUAL_CSV, uploadMultiLingualCsvSaga);
}

function* getDefaultButtonsDataWatcher() {
	yield takeLeading(GET_DEFAULT_BUTTONS_DATA, getDefaultButtonsListSaga);
}

function* getTranslatedButtonsDataWatcher() {
	yield takeLeading(GET_TRANSLATED_BUTTONS_DATA, getTranslatedButtonsDataSaga);
}

function* addButtonsTranslationLanguageWatcher() {
	yield takeLeading(POST_TRANSLATE_BUTTONS_DATA, addButtonsTranslationLanguageSaga);
}

function* changeButtonsTranslateInfoWatcher() {
	yield takeLeading(CHANGE_BUTTONS_TRANSLATE_INFO, changeButtonsTranslateInfoSaga);
}

function* updateLangugaeStatusWatcher() {
	yield takeLeading(UPDATE_LANGUGAE_STATUS, updateLangugaeStatusSaga);
}

export const multiLingualWatchers = [
	getGoogleLanguagesListWatcher,
	addTranslationLanguageWatcher,
	deleteTranslationLanguageWatcher,
	getTranslatedDataWatcher,
	changeOptionTranslateInfoWatcher,
	changeQuestionTranslateInfoWatcher,
	changeSectionTranslateInfoWatcher,
	changeQuestionSettingTranslateInfoWatcher,
	changeMultipleOpinionSectionTranslateInfoWatcher,
	changeMultipleOpinionStrokeTranslateInfoWatcher,
	getAllTranslatedLanguagesListWatcher,
	uploadMultiLingualCsvWatcher,
	getDefaultButtonsDataWatcher,
	getTranslatedButtonsDataWatcher,
	addButtonsTranslationLanguageWatcher,
	changeButtonsTranslateInfoWatcher,
	updateLangugaeStatusWatcher,
];
